import { createContext, useContext, useRef, useState } from "react";
import {
  Sparkles,
  Check,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Pencil,
  AlertTriangle,
  Loader2,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

/**
 * "Draft with AI" — one reusable, inline drafting-assist control, repeated across
 * the organization profile editor's free-text narrative fields (Option A from the
 * design handoff).
 *
 * It is a drafting AID the admin always reviews and approves: it never
 * auto-publishes, never silently replaces the field, and is never presented as a
 * source of facts. A suggestion only enters the field on an explicit "Use",
 * leaving the value fully editable; saving the profile stays a separate step.
 *
 * Prose only — this control is only ever wired to narrative fields, never to any
 * numeric, select, slider, rating, or award field. The grounding and generation
 * happen server-side in the `draft-profile-section` edge function from the org's
 * own already-entered facts.
 *
 * Compound API so the trigger can sit beside the field label and the review /
 * generating / error panels render below the field, without rewiring the field
 * input itself:
 *
 *   <DraftWithAIProvider fieldKey="ourStory" orgId={orgId} value={value}
 *                        onUseDraft={onChange} maxLength={2000}>
 *     <div className="flex items-center justify-between gap-3">
 *       <label>Our story</label>
 *       <DraftTrigger />
 *     </div>
 *     <textarea ... />            // existing field, untouched
 *     <DraftPanel />
 *   </DraftWithAIProvider>
 */

type DraftStatus = "rest" | "generating" | "review" | "error";

interface DraftContextValue {
  status: DraftStatus;
  suggestion: string;
  steer: string;
  setSteer: (v: string) => void;
  expanded: boolean;
  toggleExpanded: () => void;
  isEmpty: boolean;
  isQuote: boolean;
  value: string;
  canGenerate: boolean;
  generate: () => void;
  regenerate: () => void;
  cancel: () => void;
  discard: () => void;
  useDraft: () => void;
}

const DraftContext = createContext<DraftContextValue | null>(null);

function useDraft(): DraftContextValue {
  const ctx = useContext(DraftContext);
  if (!ctx) {
    throw new Error("DraftTrigger / DraftPanel must be used within a DraftWithAIProvider");
  }
  return ctx;
}

interface ProviderProps {
  /** profile_data storage key for this field — must match the edge function's FIELD_CONFIG. */
  fieldKey: string;
  /** Org being drafted for; the edge function re-checks membership server-side. */
  orgId?: string | null;
  /** Current field value (for the condensed "Current" reference + empty detection). */
  value: string;
  /** Called only on explicit "Use" — replaces the field value, still fully editable. */
  onUseDraft: (text: string) => void;
  /** Real field character limit (clamps the inserted draft); omit for fields with no limit. */
  maxLength?: number;
  /** Featured Quote draws a quote (serif) and never touches its attribution. */
  isQuote?: boolean;
  children: React.ReactNode;
}

export function DraftWithAIProvider({
  fieldKey,
  orgId,
  value,
  onUseDraft,
  maxLength,
  isQuote = false,
  children,
}: ProviderProps) {
  const [status, setStatus] = useState<DraftStatus>("rest");
  const [suggestion, setSuggestion] = useState("");
  const [steer, setSteer] = useState("");
  const [expanded, setExpanded] = useState(false);
  // Generation token: bumping it abandons any in-flight request (Cancel / supersede).
  const genRef = useRef(0);

  const isEmpty = (value ?? "").trim().length === 0;
  const canGenerate = !!orgId;

  const run = (steerText: string) => {
    const myGen = ++genRef.current;
    setExpanded(false);
    setStatus("generating");
    supabase.functions
      .invoke("draft-profile-section", {
        body: { fieldKey, orgId, hint: steerText.trim() || undefined },
      })
      .then(({ data, error }) => {
        if (myGen !== genRef.current) return; // cancelled or superseded
        const draft = (data as { draft?: string } | null)?.draft;
        if (error || !draft) {
          setStatus("error");
          return;
        }
        setSuggestion(draft);
        setStatus("review");
      })
      .catch(() => {
        if (myGen === genRef.current) setStatus("error");
      });
  };

  const generate = () => run(steer);
  const regenerate = () => run(steer);

  const cancel = () => {
    genRef.current++; // abandon the in-flight result
    setStatus("rest");
  };

  const discard = () => {
    genRef.current++;
    setStatus("rest");
    setSteer("");
  };

  const apply = () => {
    const text = maxLength ? suggestion.slice(0, maxLength) : suggestion;
    onUseDraft(text);
    setStatus("rest");
    setSteer("");
    toast.success(
      isQuote
        ? "Quote applied — edit and attribute it, then save."
        : "Draft applied — edit freely, then save.",
    );
  };

  const ctx: DraftContextValue = {
    status,
    suggestion,
    steer,
    setSteer,
    expanded,
    toggleExpanded: () => setExpanded((e) => !e),
    isEmpty,
    isQuote,
    value: value ?? "",
    canGenerate,
    generate,
    regenerate,
    cancel,
    discard,
    useDraft: apply,
  };

  return <DraftContext.Provider value={ctx}>{children}</DraftContext.Provider>;
}

/* ── Assist button ─────────────────────────────────────────── */
function AssistButton({
  busy,
  disabled,
  onClick,
}: {
  busy?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={busy || disabled}
      onClick={onClick}
      className="h-8 shrink-0 gap-1.5 rounded-lg bg-primary/10 px-3 text-xs font-semibold text-primary hover:bg-primary/15 hover:text-primary disabled:opacity-60"
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Sparkles className="h-3.5 w-3.5" />
      )}
      {busy ? "Drafting…" : "Draft with AI"}
    </Button>
  );
}

/* ── Field shade (wraps the field input) ───────────────────── */
// Dims (never clears) the existing field while a draft is generating, so the
// transient generating panel below is the focus. No effect in any other state.
export function DraftFieldShade({ children }: { children: React.ReactNode }) {
  const { status } = useDraft();
  return (
    <div
      className={cn(
        "motion-safe:transition-opacity motion-safe:duration-200",
        status === "generating" && "opacity-50",
      )}
    >
      {children}
    </div>
  );
}

/* ── Trigger (sits beside the field label) ─────────────────── */
export function DraftTrigger() {
  const { status, generate, canGenerate } = useDraft();

  if (status === "rest") {
    return <AssistButton onClick={generate} disabled={!canGenerate} />;
  }
  if (status === "generating") {
    return <AssistButton busy />;
  }
  if (status === "review") {
    return (
      <span className="shrink-0 text-xs font-medium text-muted-foreground">
        Draft ready to review
      </span>
    );
  }
  return null;
}

/* ── Panel (sits below the field input) ────────────────────── */
const ENTER =
  "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1 motion-safe:duration-200";

export function DraftPanel() {
  const {
    status,
    suggestion,
    steer,
    setSteer,
    expanded,
    toggleExpanded,
    isEmpty,
    isQuote,
    value,
    generate,
    regenerate,
    cancel,
    discard,
    useDraft: apply,
  } = useDraft();

  // Rest: a calm hint only when the field is empty.
  if (status === "rest") {
    if (!isEmpty) return null;
    return (
      <div className="mt-3 flex items-center gap-2 rounded-lg bg-primary/5 px-3.5 py-2.5">
        <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
        <span className="text-[13px] text-primary">
          This field is empty — a draft will fill it directly, ready for you to edit.
        </span>
      </div>
    );
  }

  if (status === "generating") {
    return (
      <div className={cn("mt-3 rounded-lg bg-primary/5 p-4", ENTER)}>
        <div className="flex items-center gap-2.5">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
          <span className="text-[13px] font-semibold text-primary">
            Writing a first draft from your story's facts…
          </span>
          <button
            type="button"
            onClick={cancel}
            className="ml-auto rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </div>
        <div className="mt-3 space-y-2" aria-hidden="true">
          {["w-full", "w-[96%]", "w-[88%]"].map((w) => (
            <div key={w} className={cn("h-2.5 rounded bg-primary/10 motion-safe:animate-pulse", w)} />
          ))}
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className={cn("mt-3 flex items-start gap-3 rounded-lg border border-border bg-card p-4", ENTER)}>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
        </span>
        <div className="flex-1">
          <p className="text-[13px] font-semibold text-foreground">
            Couldn't generate a draft just now.
          </p>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            Please try again. Your existing text hasn't changed.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={generate}
              className="h-8 gap-1.5 text-primary"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Try again
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={discard}
              className="h-8 text-muted-foreground"
            >
              Dismiss
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // status === "review"
  return (
    <div className={cn("mt-3", ENTER)}>
      {/* Condensed reference to the admin's current text */}
      {isEmpty ? (
        <p className="px-0.5 text-[12.5px] text-muted-foreground">
          This field is empty — using the draft will populate it directly.
        </p>
      ) : (
        <div className="rounded-lg border border-border bg-muted/30 px-3.5 py-2.5">
          <div className={cn("flex gap-3", expanded ? "items-start" : "items-center")}>
            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Current
            </span>
            <span
              className={cn(
                "flex-1 text-[13px] text-muted-foreground",
                expanded ? "whitespace-pre-wrap leading-relaxed text-foreground/80" : "truncate",
              )}
            >
              {value}
            </span>
            <button
              type="button"
              onClick={toggleExpanded}
              className="flex shrink-0 items-center gap-1 text-[12.5px] font-semibold text-primary"
            >
              {expanded ? "Hide" : "Show full"}
              {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      )}

      {/* Suggestion card */}
      <div className={cn("rounded-xl bg-primary/5 p-4", isEmpty ? "mt-2" : "mt-3")}>
        <div className="mb-3 flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="text-xs font-bold text-primary">
            {isQuote ? "Suggested quote" : "Suggested draft"}
          </span>
          <span className="ml-auto text-[11px] text-primary/70">Grounded in your story's facts</span>
        </div>

        <div
          className={cn(
            "rounded-lg border border-primary/10 bg-card px-4 py-3 text-foreground",
            isQuote
              ? "font-serif text-lg italic leading-relaxed"
              : "text-sm leading-relaxed",
          )}
        >
          {isQuote ? `“${suggestion}”` : suggestion}
        </div>

        {/* Actions */}
        <div className="mt-3.5 flex flex-wrap items-center gap-x-3 gap-y-2">
          <Button type="button" size="sm" onClick={apply} className="h-8 gap-1.5">
            <Check className="h-3.5 w-3.5" />
            {isQuote ? "Use this quote" : "Use this draft"}
          </Button>
          <span className="text-[12px] text-muted-foreground">
            {isQuote
              ? "Replaces the quote text only"
              : isEmpty
                ? "Fills the empty field — still fully editable"
                : "Replaces your text — still fully editable"}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={discard}
            className="ml-auto h-8 text-muted-foreground"
          >
            Discard
          </Button>
        </div>

        {/* Steer + regenerate */}
        <div className="mt-3.5">
          <div className="mb-1.5 text-xs font-semibold text-foreground">
            Adjust the draft <span className="font-normal text-muted-foreground">(optional)</span>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Pencil className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={steer}
                onChange={(e) => setSteer(e.target.value)}
                placeholder="e.g. emphasize our mentorship for new agents"
                className="h-9 bg-background pl-9 text-sm"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={regenerate}
              className="h-9 shrink-0 gap-1.5 text-primary"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Regenerate
            </Button>
          </div>
        </div>

        {/* Ownership footnote */}
        <div className="mt-3.5 flex items-center gap-2 text-[12px] text-muted-foreground">
          <Shield className="h-3.5 w-3.5 shrink-0" />
          You own and edit this draft. Nothing is published until you save.
        </div>
      </div>
    </div>
  );
}
